import React from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { TFunction } from 'react-i18next';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from 'lodash';
import {
  Pagination,
  TextField,
  List,
  Stack,
  Tooltip,
  Icon,
  Text,
  Information,
  withTranslation,
  Trans,
  StyledContainer,
} from '@piwikpro/ui-components';
import { connect, ConnectedProps } from 'react-redux';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Pagination as PaginationManager,
  view,
  withAccess,
  errorBoundary,
  withInject,
  ConfigService,
  QueryParams,
  InternalListLink,
  PermissionSelect,
} from '@piwikpro/platform';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { AccessControlService, PermissionLevel } from '@piwikpro/access-control-crate';
import { AccessControlState } from '@piwikpro/access-control-crate/redux';
import { AdministrationState } from '../../../../../redux';
import { permissionSelectOptions } from '../../../../permissionOptions';

const mapStateToProps = (state: { session: SessionState } & AdministrationState & AccessControlState) => {
  const { settings } = state.administration.apps.details.settings;

  return {
    isOwner: state.session.userRole === 'OWNER',
    app: settings as NonNullable<typeof settings>,
    isLoading: state.accessControl.app.usersPermissions.isLoading,
    permissions: state.accessControl.app.usersPermissions.permissions,
    total: state.accessControl.app.usersPermissions.total,
    failed: state.accessControl.app.usersPermissions.failed,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  accessControl: AccessControlService
  config: ConfigService
  userPreferences: any
  appPermissionLevels: PermissionLevel[]
  accessManager: any
  Title: typeof React.Component
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  routeParams: QueryParams
  pathname: string
  t: TFunction
}

interface State {
  searchDebounce: null | DebouncedFunc<() => void>
  search: string
}

class Users extends React.Component<Props, State> {
  state: State = {
    searchDebounce: null,
    search: '',
  }

  constructor(props) {
    super(props);

    this.handleLimitChange = this.handleLimitChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.renderPermissionsRow = this.renderPermissionsRow.bind(this);
    this.handleUpdatePermissions = this.handleUpdatePermissions.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  async componentDidMount() {
    await this.fetchPermissions();
  }

  async componentDidUpdate(prevProps: Props) {
    const { routeParams } = this.props;

    if (
      routeParams.page !== prevProps.routeParams.page
      || routeParams.limit !== prevProps.routeParams.limit
    ) {
      await this.fetchPermissions();
    }
  }

  async fetchPermissions() {
    const {
      accessControl,
      app,
      routeParams,
      isOwner,
    } = this.props;
    const { search } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    await accessControl.fetchUserPermissionsForApp(app.id, {
      search,
      limit: pagination.getLimit(),
      offset: pagination.offset,
    }, isOwner);
  }

  async handleLimitChange(limit) {
    const {
      routerService,
      pathname,
      accessControl,
      app,
      userPreferences,
      isOwner,
    } = this.props;
    const listPreference = new ListPreference(userPreferences.load(), '');
    const { search } = this.state;

    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    await accessControl.fetchUserPermissionsForApp(app.id, {
      search,
      limit,
      offset: 0,
    }, isOwner);

    routerService.push({ to: buildPaginatedPath(pathname, 1, limit) });
  }

  async handlePageChange(offset, pagination) {
    const { search } = this.state;
    const {
      routerService,
      pathname,
      accessControl,
      app,
      isOwner,
    } = this.props;

    await accessControl.fetchUserPermissionsForApp(app.id, {
      search,
      limit: pagination.getLimit(),
      offset,
    }, isOwner);

    routerService.push({
      to: buildPaginatedPath(
        pathname,
        pagination.getPageFromOffset(offset),
        pagination.getLimit(),
      ),
    });
  }

  handleSearchChange(event) {
    const { searchDebounce } = this.state;
    const {
      accessControl,
      app,
      routeParams,
      config,
      routerService,
      pathname,
      isOwner,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    if (searchDebounce) {
      searchDebounce.cancel();
    }

    const handleSearch = debounce(() => {
      const { search } = this.state;

      accessControl.fetchUserPermissionsForApp(app.id, {
        search,
        offset: pagination.offset,
        limit: pagination.getLimit(),
      }, isOwner);
      routerService.push({ to: buildPaginatedPath(pathname, 1, pagination.getLimit()) });
    }, config.get('LIST_SEARCH_DELAY'));

    this.setState({
      searchDebounce: handleSearch,
      search: event.target.value,
    });

    handleSearch();
  }

  async handleUpdatePermissions(values, { setSubmitting, resetForm }) {
    const { accessControl } = this.props;

    try {
      await accessControl.updateUserAppPermission(values.appId, values.userId, values.permission);
    } catch (err) {
      resetForm();
    }

    setSubmitting(false);
  }

  renderPermissionsSelector(user) {
    const {
      app,
      accessManager,
      appPermissionLevels,
      t,
    } = this.props;
    const userAccess = user.attributes.permission;
    const isUnableToChange = userAccess === 'manage' && !accessManager.isGranted({ role: 'OWNER' });
    const SelectControl = () => (
      <PermissionSelect
        initialValue={userAccess}
        options={permissionSelectOptions(appPermissionLevels, t, 'app')}
        // eslint-disable-next-line react/no-this-in-sfc
        onSubmit={(values, api) => (this.handleUpdatePermissions({
          ...values,
          userId: user.id,
          appId: app.id,
        }, api))}
        disabled={isUnableToChange}
      />
    );

    if (isUnableToChange) {
      return (
        <Tooltip content={t('accessControl.permissions.role.owner.required.info')}>
          <div style={{ width: '100%' }}>
            <SelectControl />
          </div>
        </Tooltip>
      );
    }

    return <SelectControl />;
  }

  renderPermissionsRow(user) {
    const {
      appPermissionLevels,
      isOwner,
    } = this.props;
    const userAccess = user.attributes.permission;
    const effectivePermission = appPermissionLevels.filter(level => (
      [
        userAccess,
        user.attributes.group_permissions.permission,
      ].includes(level.id)
    ));

    return (
      <List.Item
        key={user.id}
      >
        {isOwner
          ? (
            <InternalListLink
              to="/administration/users/$userId"
              params={{ userId: user.id }}
              label={user.attributes.email}
            />
          )
          : <Text as="p" wrapType="noWrapAndCutText">{user.attributes.email}</Text>}
        {this.renderPermissionsSelector(user)}
        {user.attributes.group_permissions.permission !== 'no-access' ? (
          <Tooltip content={user.attributes.group_permissions.group_names.join(', ')}>
            <div>
              <Text as="p">
                <Trans
                  i18nKey="administration:apps.details.permissions.groupPermission.label"
                  values={{
                    permissionName: appPermissionLevels.find(level => (
                      level.id === user.attributes.group_permissions.permission
                    ))?.name,
                    permissionGroup: `${user.attributes.group_permissions.group_names.slice(0, 2).join(
                      ', ',
                    )}${user.attributes.group_permissions.group_names.length > 2 ? ', ...' : ''}`,
                  }}
                  components={[
                    <Text weight={600} dataId="TextStyle">permissionName</Text>,
                    <Text weight={600} dataId="TextStyle">permissionGroup</Text>,
                  ]}
                />
              </Text>
            </div>
          </Tooltip>
        ) : null}
        <Text weight={600} dataId="TextStyle">
          {effectivePermission[effectivePermission.length - 1].name}
        </Text>
        <Stack spacing="narrow">
          {user.attributes.modules && this.renderModules(user.attributes.modules)}
        </Stack>
      </List.Item>
    );
  }

  renderModules(modules) {
    const { t } = this.props;

    return (
      <>
        <Stack.Item>
          <Text
            weight={600}
            color={modules.length === 0 ? 'Red' : undefined}
            dataId="TextStyle"
          >
            {t('apps.details.permissions.modules', { count: modules.length })}
          </Text>
        </Stack.Item>
        {modules.length !== 0 && (
          <Stack.Item>
            <Tooltip
              position="top-end"
              content={modules.map(module => t(module)).join(', ')}
            >
              <div>
                <Icon name="info-circle" />
              </div>
            </Tooltip>
          </Stack.Item>
        )}
      </>
    );
  }

  render() {
    const {
      permissions,
      isLoading,
      failed,
      routeParams,
      config,
      t,
      total,
      Title,
      isOwner,
    } = this.props;
    const {
      search,
    } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      permissions,
      total,
      routeParams,
    );

    return (
      <Title part={t('apps.details.title.permissions.users')}>
        {!failed && (
          <Stack.Item fill>
            <Information type="info">{t('apps.details.permissions.info')}</Information>
          </Stack.Item>
        )}
        <Stack.Item fill>
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                value={search}
                name="search"
                icon="search"
                onChange={this.handleSearchChange}
                placeholder={t('apps.details.permissions.search.placeholder')}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 4,
                name: t('apps.details.permissions.column.email'),
                align: 'left',
              },
              {
                size: 3,
                name: t('apps.details.permissions.column.userPermission'),
                align: 'left',
              },
              {
                size: 3,
                name: t('apps.details.permissions.column.groupPermission'),
                align: 'left',
              },
              {
                size: 3,
                name: t('apps.details.permissions.column.effectivePermission'),
                align: 'left',
              },
              ...isOwner ? [{
                size: 3,
                name: t('apps.details.permissions.column.moduleAccess'),
                align: 'left' as any,
              }] : [],
            ]}
            isLoading={isLoading}
            error={failed}
          >
            {
              !failed && permissions
                .map(userWithPermissions =>
                  this.renderPermissionsRow(userWithPermissions))
            }
          </List>
        </Stack.Item>
        {!failed && (
          <Stack.Item fill>
            <Pagination
              onLimitChange={this.handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={e => this.handlePageChange(e, pagination)}
            />
          </Stack.Item>
        )}
      </Title>
    );
  }
}

const UsersConnected = connector(withInject<InjectedProps>({
  accessControl: 'AccessControlCrate.accessControl',
  config: 'config',
  userPreferences: 'userPreferences',
  appPermissionLevels: 'appPermissionLevels',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'USER', level: 'manage' })(withAccess(withTranslation(['administration'])(
    errorBoundary(Users),
  ))),
));

const UsersWrapper = (props) => {
  const routeParams = useParams({
    from: '/_secured/administration/apps/$appId/permissions/users/$page/$limit',
  });
  const { pathname } = useLocation();

  return (
    <UsersConnected
      {...props}
      routeParams={routeParams}
      pathname={pathname}
    />
  );
};

export default UsersWrapper;
